import React, { useMemo, useState } from "react";
import Alert from "../../components/common/Alert";
import Card from "../../components/common/Card";
import FilterBar, {
  formatDateInputValue,
} from "../../components/common/FilterBar";
import { HourlyTrendChart } from "../../components/common/ReportingCharts";
import PageHeader from "../../components/common/PageHeader";
import { useGetBusinessesQuery, useGetHourlyDemandQuery } from "../../store/api";
import {
  getDefaultReportingFilters,
  getPresetDateRange,
  getStoreOptionsFromBusinesses,
  getUtcRangeFromDateInputs,
} from "../../utils/reporting";

const HourlyDemand = () => {
  const { data: businesses } = useGetBusinessesQuery();
  const storeOptions = useMemo(
    () => getStoreOptionsFromBusinesses(businesses),
    [businesses],
  );
  const [draftFilters, setDraftFilters] = useState(
    getDefaultReportingFilters(),
  );
  const [appliedFilters, setAppliedFilters] = useState(
    getDefaultReportingFilters(),
  );

  const queryArgs = useMemo(() => {
    const range = getUtcRangeFromDateInputs(appliedFilters);
    return {
      storeIds: appliedFilters.storeIds,
      from: range.from,
      to: range.to,
    };
  }, [appliedFilters]);

  const { data, isLoading, isFetching, isError, error } =
    useGetHourlyDemandQuery(queryArgs);

  const points = Array.isArray(data?.points) ? data.points : [];
  const datePresets = [
    { value: "today", label: "Today" },
    { value: "last7", label: "Last 7 Days" },
    { value: "last30", label: "Last 30 Days" },
  ];
  const activePreset =
    datePresets.find((preset) => {
      const range = getPresetDateRange(preset.value);
      return draftFilters.from === range.from && draftFilters.to === range.to;
    })?.value || "";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Hourly Demand"
        subtitle="Demand trend by hour using synced completed-order history."
      />

      <Card>
        <div className="mb-6">
          <FilterBar
            startDate={draftFilters.from}
            endDate={draftFilters.to}
            onStartDateChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                from: value ? formatDateInputValue(value) : "",
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            onEndDateChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                to: value ? formatDateInputValue(value) : "",
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            stores={storeOptions}
            storeId={draftFilters.storeIds[0] || ""}
            onStoreChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                storeIds: value ? [value] : [],
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            presets={datePresets}
            activePreset={activePreset}
            onPresetChange={(presetKey) => {
              const range = getPresetDateRange(presetKey);
              const nextFilters = {
                ...draftFilters,
                from: range.from,
                to: range.to,
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            onReset={() => {
              const next = getDefaultReportingFilters();
              setDraftFilters(next);
              setAppliedFilters(next);
            }}
            isLoading={isFetching}
          />
        </div>

        {isError && (
          <Alert tone="error">
            {error?.data?.detail || "Failed to load hourly demand."}
          </Alert>
        )}

        <div className="mb-5">
          <h2 className="text-lg font-semibold text-slate-900">Demand Trend</h2>
          <p className="text-sm text-slate-500">
            Orders are charted as a line, with net sales broken out below by
            hour slot.
          </p>
        </div>
        {isLoading ? (
          <div className="h-[320px] animate-pulse rounded-xl bg-slate-100" />
        ) : points.length === 0 ? (
          <Alert tone="info">
            No hourly demand data found for selected filters.
          </Alert>
        ) : (
          <HourlyTrendChart
            points={points}
            currencyCode={data?.currencyCode || "USD"}
          />
        )}
      </Card>
    </div>
  );
};

export default HourlyDemand;
